import { useRef, useState } from 'react'
import { applyRevision, diffRevision } from '~/api/catalog'
import type { RevisionDiff } from '~/api/types'
import { useToast } from '~/components/ui/toast'
import { MAX_FILE_BYTES, MAX_FILE_MB, type TabKey } from './constants'
import { changesToFields, refToFields } from './helpers'

// 개정본 비교 패널의 상태·부수효과(업로드 검증·diff 호출·선택·일괄 반영)를 모은 훅.
// 표시 컴포넌트는 순수하게 유지하고, 패널은 이 훅으로 오케스트레이션만 한다.
export function useRevisionDiff({
    onApplied,
}: {
    // 일괄 반영 성공 → 상위에서 목록·요약 갱신 + 재색인 안내. 토스트 메시지를 전달한다.
    onApplied?: (message: string) => void
}) {
    const toast = useToast()
    const [file, setFile] = useState<File | null>(null)
    const [dragOver, setDragOver] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<RevisionDiff | null>(null)
    const [tab, setTab] = useState<TabKey>('modified')
    const inputRef = useRef<HTMLInputElement>(null)

    // 선택 상태(반영 대상). 수정=id 집합, 추가=인덱스 집합, 삭제=id 집합(경고용).
    const [selModified, setSelModified] = useState<Set<string>>(new Set())
    const [selAdded, setSelAdded] = useState<Set<number>>(new Set())
    const [selRemoved, setSelRemoved] = useState<Set<string>>(new Set())
    const [confirming, setConfirming] = useState(false)
    const [applying, setApplying] = useState(false)

    const pickFile = (f: File | null) => {
        if (!f) return
        const name = f.name.toLowerCase()
        const okExt = name.endsWith('.xlsx') || name.endsWith('.xls')
        if (!okExt) {
            const ext = name.includes('.')
                ? name.slice(name.lastIndexOf('.'))
                : '(확장자 없음)'
            setError(
                `허용 형식은 .xlsx(평가매뉴얼)입니다. 올린 파일 형식 "${ext}"은 지원하지 않아요.`
            )
            return
        }
        if (f.size === 0) {
            setError('빈 파일입니다. 내용이 있는 평가매뉴얼 xlsx를 올려주세요.')
            return
        }
        if (f.size > MAX_FILE_BYTES) {
            setError(
                `파일이 너무 큽니다(${(f.size / 1024 / 1024).toFixed(1)}MB). 최대 ${MAX_FILE_MB}MB까지 올릴 수 있어요.`
            )
            return
        }
        setError(null)
        setResult(null)
        setFile(f)
    }

    const clearSelection = () => {
        setSelModified(new Set())
        setSelAdded(new Set())
        setSelRemoved(new Set())
    }

    const run = async () => {
        if (!file) return
        setLoading(true)
        setError(null)
        try {
            const diff = await diffRevision(file)
            setResult(diff)
            clearSelection()
            setTab(
                diff.summary.modified > 0
                    ? 'modified'
                    : diff.summary.added > 0
                      ? 'added'
                      : 'removed'
            )
        } catch (e) {
            // 파싱·서버 단계 오류는 원인을 덧붙여 운영자가 다음 행동을 알게 한다.
            const raw = (e as Error).message
            setError(
                `개정본을 비교하지 못했어요: ${raw}. 평가매뉴얼 양식(시트·머리글)이 맞는지, 손상되지 않은 .xlsx인지 확인 후 다시 시도하세요.`
            )
        } finally {
            setLoading(false)
        }
    }

    const reset = () => {
        setFile(null)
        setResult(null)
        setError(null)
        clearSelection()
        if (inputRef.current) inputRef.current.value = ''
    }

    const s = result?.summary
    const noChanges = s && s.added === 0 && s.modified === 0 && s.removed === 0

    // 반영 대상(추가+수정)만 카운트. 삭제는 자동 반영하지 않는다.
    const applyCount = selModified.size + selAdded.size

    const apply = async () => {
        if (!result || applyCount === 0) return
        setConfirming(false)
        setApplying(true)
        setError(null)
        try {
            const modified = result.modified
                .filter((m) => selModified.has(m.id))
                .map((m) => ({ id: m.id, ...changesToFields(m.changes) }))
            const added = result.added
                .filter((_, i) => selAdded.has(i))
                .map(refToFields)
            const res = await applyRevision({ modified, added })
            const msg =
                `반영 완료 — 수정 ${res.updated} · 추가 ${res.created}` +
                (res.failed.length > 0 ? ` · 실패 ${res.failed.length}` : '')
            onApplied?.(msg)
        } catch (e) {
            toast.error((e as Error).message)
            setApplying(false)
        }
    }

    return {
        file,
        dragOver,
        setDragOver,
        loading,
        error,
        result,
        tab,
        setTab,
        inputRef,
        selModified,
        setSelModified,
        selAdded,
        setSelAdded,
        selRemoved,
        setSelRemoved,
        confirming,
        setConfirming,
        applying,
        pickFile,
        run,
        reset,
        apply,
        s,
        noChanges,
        applyCount,
    }
}
